import React from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation, useParams } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { CommissionProvider } from "@/components/commission/Commission";
import AdminCommission from "@/pages/AdminCommission";
import Layout from "@/components/layout/Layout";
import Landing from "@/pages/Landing";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import VerifyEmail from "@/pages/VerifyEmail";
import DashboardRouter from "@/pages/DashboardRouter";
import SearchPrestataires from "@/pages/SearchPrestataires";
import PrestataireProfile from "@/pages/PrestataireProfile";
import MonProfil from "@/pages/MonProfil";
import MesDocuments from "@/pages/MesDocuments";
import MonCalendrier from "@/pages/MonCalendrier";
import Besoins from "@/pages/Besoins";
import NouveauBesoin from "@/pages/NouveauBesoin";
import BesoinDetail from "@/pages/BesoinDetail";
import Messages from "@/pages/Messages";
import MessagesMission from "@/pages/MessagesMission";
import MesMissions from "@/pages/MesMissions";
import Factures from "@/pages/Factures";
import Parrainage from "@/pages/Parrainage";
import NotifPreferences from "@/pages/NotifPreferences";
import AdminPanel from "@/pages/AdminPanel";
import AdminDocumentsQueue from "@/pages/AdminDocumentsQueue";
import AdminCommandesMentions from "@/pages/AdminCommandesMentions";
import MonOrganisation from "@/pages/MonOrganisation";
import AcceptInvitation from "@/pages/AcceptInvitation";
import Legal from "@/pages/Legal";
import FacturationElectronique from "@/pages/FacturationElectronique";
import Commandes from "@/pages/Commandes";
import "@/App.css";

function Protected({ children, roles }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="p-8 text-sm text-[#3E4C59]">Chargement…</div>;
  if (!user) return <Navigate to="/connexion" replace />;
  if (roles && !roles.includes(user.role)) return <Navigate to="/app/dashboard" replace />;
  return <Layout>{children}</Layout>;
}

// Renommage 2026-08-31 : besoins → chantiers côté URL uniquement (routes API,
// collections Mongo, testids intacts). Ces 3 routes de compatibilité sont
// GARDÉES INDÉFINIMENT — les e-mails déjà envoyés contiennent les anciennes
// URL et resteront dans les boîtes des utilisateurs pendant des mois. Ne
// jamais les supprimer.
function LegacyChantierRedirect({ to }) {
  const params = useParams();
  const { search, hash } = useLocation();
  const suffix = params.id ? `/${params.id}` : "";
  return <Navigate to={`${to}${suffix}${search}${hash}`} replace />;
}

function App() {
  return (
    <AuthProvider>
      <CommissionProvider>
      <BrowserRouter>
        <Toaster richColors position="top-right" />
        <Routes>
          <Route path="/" element={<Landing />} />
          <Route path="/connexion" element={<Login />} />
          <Route path="/inscription" element={<Register />} />
          <Route path="/mot-de-passe-oublie" element={<ForgotPassword />} />
          <Route path="/reinitialiser-mot-de-passe" element={<ResetPassword />} />
          <Route path="/verifier-email" element={<VerifyEmail />} />
          <Route path="/mentions-legales" element={<Legal page="mentions" />} />
          <Route path="/cgu" element={<Legal page="cgu" />} />
          <Route path="/cgv" element={<Legal page="cgv" />} />
          <Route path="/confidentialite" element={<Legal page="rgpd" />} />
          <Route path="/responsabilite-rgpd" element={<Legal page="responsabilite" />} />
          <Route path="/cookies" element={<Legal page="cookies" />} />

          <Route path="/app/dashboard" element={<Protected><DashboardRouter /></Protected>} />
          <Route path="/app/prestataires" element={<Protected roles={["entreprise", "admin"]}><SearchPrestataires /></Protected>} />
          <Route path="/app/prestataires/:id" element={<Protected><PrestataireProfile /></Protected>} />
          <Route path="/app/mon-profil" element={<Protected roles={["prestataire"]}><MonProfil /></Protected>} />
          <Route path="/app/mes-documents" element={<Protected roles={["prestataire"]}><MesDocuments /></Protected>} />
          <Route path="/app/mon-calendrier" element={<Protected roles={["prestataire"]}><MonCalendrier /></Protected>} />
          <Route path="/app/chantiers" element={<Protected><Besoins /></Protected>} />
          <Route path="/app/chantiers/nouveau" element={<Protected roles={["entreprise"]}><NouveauBesoin /></Protected>} />
          <Route path="/app/chantiers/:id" element={<Protected><BesoinDetail /></Protected>} />
          {/* Redirections legacy (URL besoins → chantiers). NE JAMAIS SUPPRIMER : les
              e-mails de notification déjà envoyés pointent encore vers ces routes. */}
          <Route path="/app/besoins" element={<LegacyChantierRedirect to="/app/chantiers" />} />
          <Route path="/app/besoins/nouveau" element={<LegacyChantierRedirect to="/app/chantiers/nouveau" />} />
          <Route path="/app/besoins/:id" element={<LegacyChantierRedirect to="/app/chantiers" />} />
          <Route path="/app/messages" element={<Protected><Messages /></Protected>} />
          <Route path="/app/missions/:missionId/messages" element={<Protected><MessagesMission /></Protected>} />
          <Route path="/app/missions" element={<Protected><MesMissions /></Protected>} />
          <Route path="/app/factures" element={<Protected><Factures /></Protected>} />
          <Route path="/app/commandes" element={<Protected roles={["entreprise", "prestataire"]}><Commandes /></Protected>} />
          <Route path="/app/facturation-electronique" element={<Protected><FacturationElectronique /></Protected>} />
          <Route path="/app/parrainage" element={<Protected roles={["entreprise"]}><Parrainage /></Protected>} />
          <Route path="/app/notifications" element={<Protected><NotifPreferences /></Protected>} />
          <Route path="/app/admin" element={<Protected roles={["admin"]}><AdminPanel /></Protected>} />
          <Route path="/app/admin/documents" element={<Protected roles={["admin"]}><AdminDocumentsQueue /></Protected>} />
          <Route path="/app/admin/commandes-mentions" element={<Protected roles={["admin"]}><AdminCommandesMentions /></Protected>} />
          <Route path="/app/admin/commission" element={<Protected roles={["admin"]}><AdminCommission /></Protected>} />
          <Route path="/app/mon-organisation" element={<Protected roles={["entreprise"]}><MonOrganisation /></Protected>} />
          <Route path="/invitation/:token" element={<AcceptInvitation />} />

          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
      </CommissionProvider>
    </AuthProvider>
  );
}
export default App;
